import type { ComponentProps } from 'react';
import type { NameGlow } from '@/lib/api/rewards-hub/models';
import { cn } from '@/lib/ui-kit/utils';

interface GlowNameProps extends ComponentProps<'span'> {
  /** `null` — the default nickname, in whatever colour the parent sets. */
  glow: NameGlow | null;
  /**
   * `auto` follows the site theme; `dark` is for a surface that stays dark in
   * the light theme too.
   */
  tone?: 'auto' | 'dark';
}

/**
 * A nickname wearing its glow-up: cut from metal (Figma 13878:192841). The
 * paint is the `.name-metal-*` classes in `globals.css`; size and weight come
 * from the parent.
 */
export function GlowName({ glow, tone = 'auto', className, children, ...props }: GlowNameProps) {
  return (
    <span
      {...props}
      className={cn(glowClass(glow), glow && tone === 'dark' && 'name-metal-on-dark', className)}
    >
      {children}
    </span>
  );
}

/** The same paint as a class, for text that cannot be wrapped in `GlowName`. */
export function glowClass(glow: NameGlow | null | undefined): string | undefined {
  if (!glow) return undefined;
  // The wire's names predate the design: GOLD (the first cup) is the silver
  // step, NEON (the first relic) the gold one.
  return glow === 'GOLD' ? 'name-metal-silver' : 'name-metal-gold';
}
